import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { SidebarAction, SidebarConfig } from './sidebar-config.interface';

@Component({
    selector: 'edu-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('fadeIn', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('200ms ease-in', style({ opacity: 1 }))
            ]),
            transition(':leave', [
                animate('200ms ease-out', style({ opacity: 0 }))
            ])
        ]),
        trigger('slideInFromRight', [
            transition(':enter', [
                style({
                    opacity: 0,
                    transform: 'translateX(100%)'
                }),
                animate('{{duration}}ms cubic-bezier(0.25, 0.8, 0.25, 1)', style({
                    opacity: 1,
                    transform: 'translateX(0)'
                }))
            ]),
            transition(':leave', [
                animate('{{duration}}ms cubic-bezier(0.25, 0.8, 0.25, 1)', style({
                    opacity: 0,
                    transform: 'translateX(100%)'
                }))
            ])
        ])
    ],
    standalone: false
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Input() config: SidebarConfig = {
    title: '',
    icon: '',
    show: false
  };

  @Output() close = new EventEmitter<void>();
  @Output() primaryAction = new EventEmitter<void>();
  @Output() secondaryAction = new EventEmitter<number>();
  @Output() leftAction = new EventEmitter<number>();

  // Default configuration
  private defaultConfig: Partial<SidebarConfig> = {
    width: '480px',
    maxWidth: '100vw',
    animationDuration: 350,
    closeOnOverlayClick: true,
    showOverlay: true,
    loading: false
  };

  ngOnInit() {
    // Merge with defaults
    this.config = { ...this.defaultConfig, ...this.config };
  }

  ngOnDestroy() {
    // Cleanup if needed
  }

  get sidebarWidth(): string {
    return this.config.width || '480px';
  }

  get sidebarMaxWidth(): string {
    return this.config.maxWidth || '100vw';
  }

  get animationParams() {
    return { 
      value: '', 
      params: { 
        duration: this.config.animationDuration || 350 
      } 
    };
  }

  get sidebarClasses(): string {
    const baseClasses = 'sidebar';
    const customClasses = this.config.customClasses?.sidebar || '';
    return `${baseClasses} ${customClasses}`.trim();
  }

  get headerClasses(): string {
    const baseClasses = 'sidebar-header';
    const customClasses = this.config.customClasses?.header || '';
    return `${baseClasses} ${customClasses}`.trim();
  }

  get contentClasses(): string {
    const baseClasses = 'sidebar-content';
    const customClasses = this.config.customClasses?.content || '';
    return `${baseClasses} ${customClasses}`.trim();
  }

  get footerClasses(): string {
    const baseClasses = 'sidebar-footer';
    const customClasses = this.config.customClasses?.footer || '';
    return `${baseClasses} ${customClasses}`.trim();
  }

  get overlayClasses(): string {
    const baseClasses = 'sidebar-overlay';
    const customClasses = this.config.customClasses?.overlay || '';
    return `${baseClasses} ${customClasses}`.trim();
  }

  get hasActions(): boolean {
    return !!(this.config.actions?.primary || 
              this.config.actions?.secondary?.length || 
              this.config.actions?.leftActions?.length);
  }

  onOverlayClick(): void {
    if (this.config.closeOnOverlayClick !== false) {
      this.onClose();
    }
  }

  onClose(): void {
    this.close.emit();
  }

  onPrimaryActionClick(): void {
    if (this.config.actions?.primary) {
      this.config.actions.primary.action();
      this.primaryAction.emit();
    }
  }

  onSecondaryActionClick(index: number): void {
    if (this.config.actions?.secondary?.[index]) {
      this.config.actions.secondary[index].action();
      this.secondaryAction.emit(index);
    }
  }

  onLeftActionClick(index: number): void {
    if (this.config.actions?.leftActions?.[index]) {
      this.config.actions.leftActions[index].action();
      this.leftAction.emit(index);
    }
  }

  getButtonType(action: SidebarAction): string {
    return `mat-${action.type || 'raised'}-button`;
  }

  getButtonColor(action: SidebarAction): string | undefined {
    return action.color;
  }
}
